import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounce } from "@uidotdev/usehooks";

import Modal from "../../layouts/Modal";
import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useCustomerUsers from "../../data/CustomerUser/useCustomerUsers";
import useTicketAttachments from "../../data/Ticket/useTicketAttachments";
import useTicketMergePreview from "../../data/Ticket/useTicketMergePreview";
import useTicketMutations from "../../data/Ticket/useTicketMutations";
import { ITicket } from "../../models/Tickets/Ticket";
import { errorResolver } from "../../utils/errorResolver";
import TicketChatThread from "./TicketChatThread";
import { TicketPriorityPill, TicketTypePill } from "./ticketMeta";

/** Mirrors TicketBulkRules.MaxMergeTickets on the API. */
const maxMergeTickets = 50;

interface TicketMergeModalProps {
  /** The selection when the merge was started - kept apart from the live table selection. */
  tickets: ITicket[];
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getCustomerName: (customerId: string) => string;
  getStaffName: (email?: string) => string;
  /** Called once the merge has saved. */
  onMerged: () => void;
}

export default function TicketMergeModal({
  tickets,
  open,
  setOpen,
  getCustomerName,
  getStaffName,
  onMerged,
}: TicketMergeModalProps) {
  const { merge } = useTicketMutations();
  const [subject, setSubject] = useState("");
  const [mergedTicket, setMergedTicket] = useState<ITicket | null>(null);

  const ordered = useMemo(
    () =>
      [...tickets].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
      ),
    [tickets],
  );
  const ticketIds = useMemo(() => ordered.map((x) => x.id), [ordered]);
  const customerIds = useMemo(
    () => Array.from(new Set(tickets.map((x) => x.customerId))),
    [tickets],
  );
  const customerId = customerIds.length === 1 ? customerIds[0] : "";
  const defaultSubject = ordered[0]?.subject ?? "";

  // Each opening starts from the earliest ticket's subject, which is also what
  // the API falls back to.
  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
    setMergedTicket(null);
    merge.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Checked here as well as on the API so the preview isn't requested for a
  // selection that can't be merged.
  const selectionProblem =
    tickets.length < 2
      ? "Select at least two tickets to merge."
      : tickets.length > maxMergeTickets
        ? `Merges are limited to ${maxMergeTickets} tickets at a time.`
        : customerIds.length > 1
          ? "Only tickets from the same customer can be merged - the merged conversation is visible to the customer."
          : null;

  // The default subject is sent as blank, so opening the modal and settling on
  // the default both land on the same preview request.
  const debouncedSubject = useDebounce(subject.trim(), 400);
  const previewSubject =
    debouncedSubject === defaultSubject ? "" : debouncedSubject;

  const { preview } = useTicketMergePreview(
    ticketIds,
    previewSubject,
    open && !selectionProblem && !mergedTicket,
  );
  const { customerUsers } = useCustomerUsers(customerId);
  const { previewUrls } = useTicketAttachments(
    undefined,
    preview.data?.attachments,
  );

  // Matches the peek modal: name the person who raised the earliest ticket.
  const openingAuthor = (() => {
    const raiser = customerUsers.data?.find(
      (x) => x.id === preview.data?.createdByUserId,
    );
    if (raiser) {
      return `${raiser.forename} ${raiser.surname}`.trim() || raiser.username;
    }
    return customerId ? getCustomerName(customerId) : "Customer";
  })();

  const internalNoteCount =
    preview.data?.notes?.filter((x) => x.isInternal).length ?? 0;

  const handleMerge = () =>
    merge.mutate(
      { ticketIds, subject: subject.trim() },
      {
        onSuccess: (response) => {
          setMergedTicket(response.data);
          onMerged();
        },
      },
    );

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">
            Merge {tickets.length} tickets
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Every message, reply, internal note and attachment goes into one new
            ticket{customerId ? ` for ${getCustomerName(customerId)}` : ""}. The
            originals are closed with a note pointing to it. Nobody is emailed.
          </p>
        </div>

        {mergedTicket ? (
          <>
            <Alert
              severity="success"
              title="Tickets merged"
              message={`"${mergedTicket.subject}" now holds everything, and the ${tickets.length} original tickets are closed.`}
            />
            <div className="flex items-center justify-between border-t pt-3">
              <Link
                to={`/tickets/${mergedTicket.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-orange-700 hover:text-orange-900"
              >
                Open merged ticket
              </Link>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          </>
        ) : selectionProblem ? (
          <>
            <Alert
              severity="warn"
              title="These tickets can't be merged"
              message={selectionProblem}
            />
            <div className="flex justify-end border-t pt-3">
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <label
                htmlFor="ticket-merge-subject"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Subject
              </label>
              <input
                id="ticket-merge-subject"
                type="text"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={subject}
                placeholder={defaultSubject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {preview.isLoading ? (
              <div className="flex justify-center py-10">
                <LoadingWheel />
              </div>
            ) : preview.isError ? (
              <Alert
                severity="error"
                title="Couldn't build the preview"
                message={errorResolver(preview.error)}
              />
            ) : preview.data ? (
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span>Preview</span>
                  <TicketTypePill type={preview.data.type} />
                  <TicketPriorityPill priority={preview.data.priority} />
                  <span>
                    {preview.data.assignedToUserId
                      ? `Assigned to ${getStaffName(preview.data.assignedToUserId)}`
                      : "Unassigned"}
                  </span>
                </div>
                <TicketChatThread
                  notes={preview.data.notes ?? []}
                  opening={{
                    author: openingAuthor,
                    subject: subject.trim() || defaultSubject,
                    description: preview.data.description,
                    created: preview.data.created,
                  }}
                  attachments={preview.data.attachments}
                  attachmentPreviewUrls={previewUrls}
                  className="max-h-[45vh]"
                />
                {internalNoteCount > 0 ? (
                  <p className="mt-2 text-xs text-gray-500">
                    {internalNoteCount} internal note
                    {internalNoteCount === 1 ? "" : "s"} will be carried over
                    too, including a record of this merge.
                  </p>
                ) : null}
              </div>
            ) : null}

            {merge.isError ? (
              <Alert
                severity="error"
                title="Merge failed"
                message={errorResolver(merge.error)}
              />
            ) : null}

            <div className="flex items-center justify-end gap-2 border-t pt-3">
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleMerge}
                disabled={!preview.data || merge.isPending}
              >
                {merge.isPending
                  ? "Merging..."
                  : `Merge ${tickets.length} tickets`}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
